// src/Component/Register.tsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Phone, User, Mail, ShieldCheck, Zap, Activity, Plus, Hash, MapPin, Image as ImageIcon, Info } from 'lucide-react';

const Register: React.FC = () => {
  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    aadharNumber: '',
    panNumber: '',
    city: '',
  });

  const [photo, setPhoto] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePos({ x, y });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;

    // Formatting Logic
    if (name === 'ifscCode') value = value.toUpperCase().slice(0, 11);
    if (name === 'panNumber') value = value.toUpperCase().slice(0, 10);
    if (name === 'mobile') value = value.replace(/\D/g, '').slice(0, 10);
    if (name === 'accountNumber') value = value.replace(/\D/g, '').slice(0, 18);
    if (name === 'aadharNumber') {
      let raw = value.replace(/\D/g, '').slice(0, 12);
      value = raw.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
    }

    setData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPhoto(e.target.files[0]);
      if (errors.photo) setErrors((prev) => ({ ...prev, photo: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (data.firstName.trim().length < 2) newErrors.firstName = "At least 2 characters";
    if (data.lastName.trim().length < 2) newErrors.lastName = "At least 2 characters";
    if (!data.email.endsWith('@gmail.com')) newErrors.email = "Must be @gmail.com";
    if (data.mobile.length !== 10) newErrors.mobile = "Invalid 10-digit number";
    if (data.accountHolderName.trim().length < 2) newErrors.accountHolderName = "Required";
    if (data.accountNumber.length < 9) newErrors.accountNumber = "Invalid length";
    if (data.ifscCode.length !== 11) newErrors.ifscCode = "Must be 11 characters";
    if (data.aadharNumber.replace(/\s/g, '').length !== 12) newErrors.aadharNumber = "12 digits required";
    if (data.panNumber.length !== 10) newErrors.panNumber = "10 characters required";
    if (!data.city) newErrors.city = "City is required";
    if (!photo) newErrors.photo = "Please upload photo";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formData = new FormData();
    Object.entries(data).forEach(([key, val]) => formData.append(key, val));
    if (photo) formData.append('passportPhoto', photo);

    try {
      const resp = await axios.post('/lead/create-lead', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (resp.status === 201 || resp.status === 200) {
        Swal.fire({ title: 'Success!', text: 'Form Submitted Successfully', icon: 'success' });
        navigate('/thankyou');
      }
    } catch (error) {
      Swal.fire({ title: 'Error!', text: 'Form Submission Failed', icon: 'error' });
    }
  };

  const techShapes = [
    { type: 'square', top: '15%', left: '10%', size: 40, delay: 0, color: 'border-blue-400/40', speed: 20 },
    { type: 'plus', top: '25%', left: '80%', size: 24, delay: 0.5, color: 'text-indigo-400/50', speed: -15 },
    { type: 'circle', top: '70%', left: '15%', size: 60, delay: 1, color: 'border-cyan-400/30', speed: 30 },
    { type: 'dot', top: '80%', left: '85%', size: 12, delay: 1.5, color: 'bg-blue-500/40', speed: -25 },
  ];

  return (
    <section className="relative py-16 md:py-24 bg-[#f8fafc] overflow-hidden font-[Urbanist]">
      
      {/* BACKGROUND ELEMENTS */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <motion.div animate={{ scale: [1, 1.1, 1], x: [0, 40, 0] }} transition={{ duration: 15, repeat: Infinity }} className="absolute -top-[10%] -left-[10%] w-[50vw] h-[50vw] rounded-full bg-blue-300/20 blur-[100px]" />
        <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:32px_32px] opacity-[0.4]"></div>
        {techShapes.map((shape, i) => (
          <motion.div key={i} className="absolute" style={{ top: shape.top, left: shape.left }} animate={{ x: mousePos.x * shape.speed, y: mousePos.y * shape.speed }}>
            {shape.type === 'square' && <div className={`border-2 ${shape.color} rounded-lg`} style={{ width: shape.size, height: shape.size }} />}
            {shape.type === 'plus' && <Plus className={shape.color} size={shape.size} />}
          </motion.div>
        ))}
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-16">
          
          {/* LEFT COLUMN */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} className="w-full lg:w-[40%] space-y-8 lg:sticky lg:top-10">
            <span className="inline-flex items-center gap-2 py-2 px-5 rounded-full bg-white/60 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest backdrop-blur-md">
              <Zap size={14} className="fill-blue-500" /> Power your trades
            </span>
            <h1 className="text-4xl md:text-6xl font-black text-slate-900 leading-tight">
              Advantage <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-700">Motilal Algo</span> Setup
            </h1>
            <p className="text-lg text-slate-600 font-medium leading-relaxed">Lightning-fast algo execution and zero manual errors. Join <strong className="text-blue-700">10,000+ traders</strong> daily.</p>
            <div className="flex gap-4 p-5 bg-white/70 rounded-3xl border border-white shadow-xl backdrop-blur-md max-w-xs">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center"><Activity className="text-blue-600" /></div>
              <div>
                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Accuracy</div>
                <div className="text-2xl font-black text-slate-900">88%</div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: COMPACT FORM */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} className="w-full lg:w-[60%] bg-white/80 border border-white rounded-[2.5rem] p-6 md:p-10 shadow-2xl backdrop-blur-xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-black text-slate-900">Get <span className="text-blue-600">Free Demat Account</span></h2>
              <p className="text-slate-500 font-medium">Complete your registration in 5 minutes</p>
            </div>

            <form onSubmit={handleOnSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              
              {/* Row: First Name & Last Name */}
              <InputField label="First Name" name="firstName" value={data.firstName} onChange={handleOnChange} error={errors.firstName} icon={<User size={18}/>} placeholder="First name" />
              <InputField label="Last Name" name="lastName" value={data.lastName} onChange={handleOnChange} error={errors.lastName} icon={<User size={18}/>} placeholder="Last name" />

              {/* Row: Email Address & Mobile Number */}
              <InputField label="Email Address" name="email" value={data.email} onChange={handleOnChange} error={errors.email} icon={<Mail size={18}/>} placeholder="example@gmail.com" />
              <InputField label="Mobile Number" name="mobile" value={data.mobile} onChange={handleOnChange} error={errors.mobile} icon={<Phone size={18}/>} placeholder="10-digit number" />

              {/* Row: Account Holder Name (Full Width) */}
              <div className="md:col-span-2">
                <InputField label="Account Holder Name" name="accountHolderName" value={data.accountHolderName} onChange={handleOnChange} error={errors.accountHolderName} icon={<User size={18}/>} placeholder="Name as per bank records" />
              </div>

              {/* Row: Account Number & IFSC */}
              <InputField label="Account Number" name="accountNumber" value={data.accountNumber} onChange={handleOnChange} error={errors.accountNumber} icon={<Hash size={18}/>} placeholder="A/C Number" />
              <InputField label="IFSC Code" name="ifscCode" value={data.ifscCode} onChange={handleOnChange} error={errors.ifscCode} icon={<Zap size={18}/>} placeholder="11 chars" />

              {/* Row: Aadhar & PAN */}
              <InputField label="Aadhar Number" name="aadharNumber" value={data.aadharNumber} onChange={handleOnChange} error={errors.aadharNumber} icon={<ShieldCheck size={18}/>} placeholder="XXXX XXXX XXXX" />
              <InputField label="PAN Number" name="panNumber" value={data.panNumber} onChange={handleOnChange} error={errors.panNumber} icon={<ShieldCheck size={18}/>} placeholder="10 chars" />

              {/* Row: City & Photo Upload */}
              <InputField label="City" name="city" value={data.city} onChange={handleOnChange} error={errors.city} icon={<MapPin size={18}/>} placeholder="Enter city" />
              
              <div className="relative group">
                <label className="block text-[13px] font-bold text-slate-600 mb-1.5 tracking-wide">Passport Size Photo</label>
                <div className={`relative flex items-center bg-slate-50/80 border ${errors.photo ? 'border-red-400' : 'border-slate-200'} rounded-xl p-3 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all`}>
                  <ImageIcon size={18} className="text-blue-500/70 mr-3" />
                  <input type="file" accept="image/*" onChange={handleFileChange} className="w-full text-xs font-semibold file:hidden cursor-pointer" />
                  <span className="absolute right-4 text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-md pointer-events-none">
                    {photo ? 'Selected' : 'Choose'}
                  </span>
                </div>
                {errors.photo && <p className="text-red-500 text-[10px] font-bold mt-1">{errors.photo}</p>}
              </div>

              {/* PHOTO GUIDELINES BOX */}
              <div className="md:col-span-2 bg-blue-50/50 border border-blue-100 rounded-2xl p-4 flex gap-3">
                <Info size={18} className="text-blue-600 shrink-0 mt-0.5" />
                <div className="text-[11px] leading-relaxed text-slate-600">
                  <p className="font-bold text-blue-800 uppercase mb-1">Upload Guidelines:</p>
                  Ensure face is fully visible. <span className="font-bold">No caps, masks, or sunglasses allowed.</span> Eyes must be open and looking straight at the camera.
                </div>
              </div>

              <div className="md:col-span-2 pt-4">
                <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }} type="submit" className="w-full bg-slate-900 text-white font-black py-4 rounded-xl shadow-lg uppercase tracking-widest text-sm flex items-center justify-center gap-2">
                  Open Account <Zap size={16} className="fill-white" />
                </motion.button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Reusable Input Sub-component
const InputField = ({ label, name, value, onChange, error, icon, placeholder }: any) => (
  <div className="relative group">
    <label htmlFor={name} className="block text-[13px] font-bold text-slate-600 mb-1.5 tracking-wide">{label}</label>
    <div className="relative">
      <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-transform group-focus-within:scale-110 ${error ? 'text-red-500' : 'text-blue-500/70'}`}>{icon}</div>
      <input 
        name={name} value={value} onChange={onChange} id={name} placeholder={placeholder}
        className={`w-full pl-11 pr-4 py-3 bg-slate-50/80 border ${error ? 'border-red-400 focus:ring-red-500/10' : 'border-slate-200 focus:ring-blue-500/10 focus:border-blue-500'} rounded-xl text-slate-800 text-sm font-semibold transition-all outline-none focus:ring-4`} 
      />
    </div>
    {error && <p className="text-red-500 text-[10px] font-bold mt-1">{error}</p>}
  </div>
);

export default Register;